import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Floating «···» menu anchored to a row button (iTruck_Search_Unified.html `actions.more`).
 * `items` = [{ label, onClick }]; closes on outside click, Escape, scroll/resize and after a choice.
 * Arrow keys move between items; focus returns to the button on close.
 */
export default function RowMenu({ anchor, items, onClose, label = "Действия со строкой" }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!anchor || !ref.current) return;
    const r = anchor.getBoundingClientRect();
    const w = ref.current.offsetWidth;
    const h = ref.current.offsetHeight;
    const left = Math.max(8, Math.min(window.innerWidth - w - 8, r.right - w));
    const below = r.bottom + 4;
    const top = below + h > window.innerHeight - 8 ? Math.max(8, r.top - h - 4) : below;
    setPos({ top, left });
  }, [anchor]);

  const close = useCallback(
    (refocus = true) => {
      onClose();
      if (refocus && anchor && anchor.isConnected) anchor.focus();
    },
    [anchor, onClose]
  );

  useEffect(() => {
    ref.current?.querySelector("button")?.focus();
    const down = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !(anchor && anchor.contains(e.target))) close(false);
    };
    const key = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      } else if (e.key === "Tab") {
        close(false);
      } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const btns = [...ref.current.querySelectorAll("button")];
        const i = btns.indexOf(document.activeElement);
        btns[(i + (e.key === "ArrowDown" ? 1 : -1) + btns.length) % btns.length]?.focus();
      }
    };
    const away = () => close(false);
    document.addEventListener("mousedown", down);
    document.addEventListener("keydown", key, true);
    window.addEventListener("resize", away);
    window.addEventListener("scroll", away, true);
    return () => {
      document.removeEventListener("mousedown", down);
      document.removeEventListener("keydown", key, true);
      window.removeEventListener("resize", away);
      window.removeEventListener("scroll", away, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div aria-label={label} className="row-menu" ref={ref} role="menu" style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: "hidden" }}>
      {items.map((it) => (
        <button
          key={it.label}
          onClick={() => {
            onClose();
            it.onClick();
          }}
          role="menuitem"
          type="button"
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

/** `const { menu, openMenu, closeMenu } = useRowMenu()`: `openMenu(event, payload)` on the «···» button. */
export function useRowMenu() {
  const [menu, setMenu] = useState(null);
  const openMenu = useCallback((e, payload) => {
    const el = e.currentTarget;
    setMenu((m) => (m && m.anchor === el ? null : { anchor: el, payload }));
    el.setAttribute("aria-haspopup", "menu");
  }, []);
  const closeMenu = useCallback(() => setMenu(null), []);
  return { menu, openMenu, closeMenu };
}

/** Rows hidden from the results («Скрыть из результатов»): ids kept in localStorage per user and list kind. */
export function useHiddenRows(kind, userId) {
  const key = `itruck:hidden:${kind}:${userId ?? "anon"}`;
  const read = () => {
    try {
      const v = JSON.parse(window.localStorage.getItem(key) || "[]");
      return Array.isArray(v) ? v.map(String) : [];
    } catch (e) {
      return [];
    }
  };
  const [ids, setIds] = useState(read);
  useEffect(() => {
    setIds(read());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const save = (next) => {
    setIds(next);
    try {
      window.localStorage.setItem(key, JSON.stringify(next));
    } catch (e) {
      /* private mode: hidden for this visit only */
    }
  };
  return {
    isHidden: (id) => ids.includes(String(id)),
    hide: (id) => !ids.includes(String(id)) && save([...ids, String(id)]),
    showAll: () => save([]),
    count: ids.length,
  };
}
